import { api, API_CONFIG } from './api.js';
import { ENDPOINTS } from '../config/endpoints.js';
import { tokenService } from './tokenService.js';
import { auth, googleProvider } from '../config/firebaseConfig.js';
import { signInWithPopup } from 'firebase/auth';

/**
 * Serviço de autenticação
 */
export const authService = {
  /**
   * Realiza o login em duas etapas
   * 1. Obtém o token da aplicação
   * 2. Autentica o usuário
   */
  async login(email, password) {
    try {
      // Passo 1: Obter token da aplicação
      const appLoginResponse = await api.post(ENDPOINTS.APP_LOGIN, {
        api_key: API_CONFIG.API_KEY,
      });

      if (appLoginResponse.data.error) {
        console.log(appLoginResponse.data);
        throw new Error('Erro ao autenticar aplicação');
      }

      const appToken = appLoginResponse.data.response;
      
      // Salvar token da aplicação
      tokenService.setAppToken(appToken);

      // Passo 2: Autenticar usuário
      const userLoginResponse = await api.post(ENDPOINTS.USER_LOGIN, {
        id: null,
        name: null,
        image: null,
        email: email,
        password: password,
        phone: null,
        provider: 'local',
        token: null,
        entities: [],
      });

      if (userLoginResponse.data.error) {
        const errorMessage = userLoginResponse.data.response || 'Credenciais inválidas';
        throw new Error(errorMessage);
      }

      const userData = userLoginResponse.data.response;
      
      // Extrair ou gerar token do usuário
      // Se a API retornar um token específico, usar ele
      const userToken = userData.token || appToken;
      
      // Salvar token do usuário
      tokenService.setUserToken(userToken);
      
      // Salvar dados do usuário
      tokenService.setUserData(userData);
      
      return {
        success: true,
        user: userData,
        appToken,
        userToken,
      };
    } catch (error) {
      tokenService.clearAll();
      
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.response || error.message || 'Erro ao fazer login com Google',
      };
    }
  },

  /**
   * Realiza logout
   */
  logout() {
    tokenService.clearAll();
  },

  /**
   * Verifica se o usuário está autenticado
   */
  isAuthenticated() {
    return tokenService.isAuthenticated();
  },

  /**
   * Obtém os dados do usuário armazenados
   */
  getCurrentUser() {
    return tokenService.getUserData();
  },

  /**
   * Busca os dados atualizados do usuário no servidor
   */
  async refreshUserData() {
    try {
      const userToken = tokenService.getUserToken();
      
      if (!userToken) {
        throw new Error('Usuário não autenticado');
      }

      // Fazer a requisição para obter dados atualizados
      const response = await api.get(ENDPOINTS.USERS, {
        headers: {
          'Authorization': userToken,
        },
      });

      if (response.data.error) {
        throw new Error(response.data.message || 'Erro ao buscar dados do usuário');
      }

      const userData = response.data.response;
      
      // Atualizar dados do usuário no localStorage
      tokenService.setUserData(userData);
      
      return {
        success: true,
        user: userData,
      };
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao buscar dados do usuário',
      };
    }
  },

  /**
   * Atualiza os dados do usuário
   */
  async updateUser(userData) {
    try {
      const userToken = tokenService.getUserToken();
      
      if (!userToken) {
        throw new Error('Usuário não autenticado');
      }

      // Fazer a requisição de atualização
      const response = await api.put(ENDPOINTS.USERS, userData, {
        headers: {
          'Authorization': userToken,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.error) {
        throw new Error(response.data.message || 'Erro ao atualizar usuário');
      }

      const updatedUser = response.data.response;
      
      // Atualizar dados do usuário no localStorage
      tokenService.setUserData(updatedUser);
      
      return {
        success: true,
        user: updatedUser,
      };
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      throw {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao atualizar perfil',
      };
    }
  },

  /**
   * Registra um novo usuário
   */
  async signup(userData, createEntity = false) {
    try {
      // Passo 1: Obter token da aplicação
      const appLoginResponse = await api.post(ENDPOINTS.APP_LOGIN, {
        api_key: API_CONFIG.API_KEY,
      });

      if (appLoginResponse.data.error) {
        throw new Error('Erro ao autenticar aplicação');
      }

      const appToken = appLoginResponse.data.response;
      tokenService.setAppToken(appToken);

      // Passo 2: Criar usuário
      const signupResponse = await api.post(ENDPOINTS.USER_CREATE, {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        phone: userData.provider === 'google' ? null : userData.phone,
        provider: userData.provider || 'local',
        image: userData.image || null,
      });

      if (signupResponse.data.error) {
        const errorMessage = signupResponse.data.response || 'Erro ao criar conta';
        throw new Error(errorMessage);
      }

      const newUser = signupResponse.data.response;

      // Passo 3: Salvar token e dados — email ainda pode não estar verificado
      const userToken = newUser.token || appToken;
      tokenService.setUserToken(userToken);
      tokenService.setUserData(newUser);

      return {
        success: true,
        user: newUser,
        appToken,
        userToken,
        emailVerified: newUser.emailVerified !== false,
      };
    } catch (error) {
      tokenService.clearAll();
      
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao atualizar perfil',
      };
    }
  },

  /**
   * Verifica o email com o código de 6 dígitos
   */
  async verifyEmail(email, code) {
    try {
      const response = await api.post(ENDPOINTS.USER_VERIFY_EMAIL, {
        email: email.trim(),
        code: String(code).trim(),
      });

      if (response.data.error) {
        throw new Error(response.data.response || 'Código inválido');
      }

      // Atualizar dado local para refletir verificação bem-sucedida
      const currentUser = tokenService.getUserData();
      if (currentUser) {
        tokenService.setUserData({ ...currentUser, emailVerified: true });
      }

      return { success: true };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.response || error.message || 'Erro ao verificar email',
      };
    }
  },

  /**
   * Reenvia o código de verificação de email
   */
  async resendVerificationEmail(email) {
    try {
      const response = await api.post(ENDPOINTS.USER_VERIFY_EMAIL_RESEND, {
        email: email.trim(),
      });

      if (response.data.error) {
        throw new Error(response.data.response || 'Erro ao reenviar código');
      }

      return { success: true };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.response || error.message || 'Erro ao reenviar código',
      };
    }
  },

  /**
   * Registra um novo usuário usando Google
   */
  async signupWithGoogle(createEntity = false) {
    try {
      // Passo 1: Autenticar com Google
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Passo 2: Criar usuário com dados do Google
      return await this.signup({
        name: user.displayName,
        email: user.email,
        password: user.uid, // Usar UID do Google como senha
        phone: user.phoneNumber || '',
        image: user.photoURL,
        provider: 'google',
      }, createEntity);
    } catch (error) {
      console.error('Google Sign In error:', error);
      throw {
        success: false,
        message: error.response?.data?.msg || error.message || 'Erro ao fazer login com Google',
      };
    }
  },

  /**
   * Faz login usando Google
   */
  async loginWithGoogle() {
    try {
      // Passo 1: Autenticar com Google
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Passo 2: Fazer login com dados do Google
      // Obter token da aplicação
      const appLoginResponse = await api.post(ENDPOINTS.APP_LOGIN, {
        api_key: API_CONFIG.API_KEY,
      });

      if (appLoginResponse.data.error) {
        throw new Error('Erro ao autenticar aplicação');
      }

      const appToken = appLoginResponse.data.response;
      tokenService.setAppToken(appToken);

      // Autenticar usuário com provider Google
      const userLoginResponse = await api.post(ENDPOINTS.USER_LOGIN, {
        id: null,
        name: user.displayName,
        image: user.photoURL,
        email: user.email,
        password: user.uid, // Usar UID do Google como senha
        phone: user.phoneNumber || null,
        provider: 'google',
        token: null,
        entities: [],
      });

      if (userLoginResponse.data.error) {
        const errorMessage = userLoginResponse.data.response || 'Credenciais inválidas';
        throw new Error(errorMessage);
      }

      const userData = userLoginResponse.data.response;
      const userToken = userData.token || appToken;
      
      tokenService.setUserToken(userToken);
      tokenService.setUserData(userData);
      
      return {
        success: true,
        user: userData,
        appToken,
        userToken,
      };
    } catch (error) {
      console.error('Google Login error:', error);
      tokenService.clearAll();
      
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.response || error.message || 'Erro ao criar conta',
      };
    }
  },

  /**
   * Inicia o processo de recuperação de senha
   * Envia um código de recuperação para o email do usuário
   */
  async requestPasswordReset(email) {
    try {
      // Obter token da aplicação
      const appToken = tokenService.getAppToken();
      
      if (!appToken) {
        // Se não existir, fazer login da aplicação
        const appLoginResponse = await api.post(ENDPOINTS.APP_LOGIN, {
          api_key: API_CONFIG.API_KEY,
        });

        if (appLoginResponse.data.error) {
          throw new Error('Erro ao autenticar aplicação');
        }

        tokenService.setAppToken(appLoginResponse.data.response);
      }

      // Solicitar código de recuperação
      const response = await api.post(ENDPOINTS.RESET_PASSWORD, {
        email: email.trim(),
      });

      if (response.data.error) {
        throw new Error(response.data.response || 'Erro ao solicitar recuperação de senha');
      }

      return {
        success: true,
        code: response.data.response?.code, // Para debug, em produção não deveria retornar
        message: 'Código de recuperação enviado para seu email',
      };
    } catch (error) {
      console.error('Password reset request error:', error);
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.response || error.message || 'Erro ao solicitar recuperação de senha',
      };
    }
  },

  /**
   * Valida o código de recuperação de senha
   */
  async validateResetCode(email, code) {
    try {
      const appToken = tokenService.getAppToken();
      
      if (!appToken) {
        throw new Error('Token de aplicação não encontrado');
      }

      // Validar código
      const response = await api.post(ENDPOINTS.RESET_PASSWORD_VALIDATE, {
        email: email.trim(),
        code: code.trim(),
      });

      if (response.data.error) {
        throw new Error(response.data.response || 'Código inválido ou expirado');
      }

      return {
        success: true,
        message: response.data.response || 'Código validado com sucesso',
      };
    } catch (error) {
      console.error('Code validation error:', error);
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.response || error.message || 'Código inválido ou expirado',
      };
    }
  },

  /**
   * Atualiza a senha do usuário após validação do código
   */
  async updatePassword(email, code, newPassword) {
    try {
      const appToken = tokenService.getAppToken();
      
      if (!appToken) {
        throw new Error('Token de aplicação não encontrado');
      }

      // Atualizar senha
      const response = await api.put(ENDPOINTS.UPDATE_PASSWORD, {
        email: email.trim(),
        code: code.trim(),
        newPassword,
      });

      if (response.data.error) {
        throw new Error(response.data.response || 'Erro ao atualizar senha');
      }

      return {
        success: true,
        message: response.data.response || 'Senha atualizada com sucesso',
      };
    } catch (error) {
      console.error('Password update error:', error);
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.response || error.message || 'Erro ao atualizar senha',
      };
    }
  },
};
